import React, { useContext, useEffect, useMemo, useState } from 'react';
import { GlobalAppContext } from 'src/components/App';
import AIInsightsService from 'src/services/ai-insights/AIInsightsService';
import { AI_INSIGHTS_FLAG, getFeatureFlags, isFeatureEnabledForUser } from 'src/services/FeatureFlagsService';

export interface AIInsightsContextType {
    /** True when aiInsightsEnabled is on for the signed-in user. */
    enabled: boolean;
    /** False until the feature flag has resolved, so consumers can avoid a flash of UI. */
    resolved: boolean;
    /** Alias of the signed-in user, read from GlobalAppContext rather than threaded as a prop. */
    userEmail: string;
    /** Injected so the chat hook never imports ApiCallService directly. */
    aiInsightsService: AIInsightsService;
}

export const AIInsightsContext = React.createContext<AIInsightsContextType | null>(null);

/**
 * Resolves the AI Insights feature flag once for the whole app and exposes it alongside the
 * signed-in user and the domain service.
 *
 * This exists so the flag is not re-resolved per consumer: the variance report renders one
 * trigger per row (page sizes go up to 500), and each instance resolving the flag itself
 * would fan out hundreds of concurrent lookups on first paint.
 *
 * Mounted inside GlobalAppContext.Provider, since it reads userProfile and services from it.
 */
export function AIInsightsProvider({ children }: { children: React.ReactNode }) {
    const { userProfile, services } = useContext(GlobalAppContext);
    const [enabled, setEnabled] = useState(false);
    const [resolved, setResolved] = useState(false);
    const userEmail = userProfile?.user || '';

    useEffect(() => {
        let cancelled = false;
        getFeatureFlags()
            .then(flags => {
                if (cancelled) return;
                setEnabled(isFeatureEnabledForUser(flags, AI_INSIGHTS_FLAG, userEmail));
            })
            .catch(err => {
                console.warn('AI Insights: failed to resolve feature flag, keeping it off', err);
                if (!cancelled) setEnabled(false);
            })
            .finally(() => {
                if (!cancelled) setResolved(true);
            });
        return () => { cancelled = true; };
    }, [userEmail]);

    const value = useMemo(() => ({
        enabled,
        resolved,
        userEmail,
        aiInsightsService: services?.aiInsightsService,
    } as AIInsightsContextType), [enabled, resolved, userEmail, services]);

    return (
        <AIInsightsContext.Provider value={value}>
            {children}
        </AIInsightsContext.Provider>
    );
}

/**
 * Reads the AI Insights context. Returns a disabled value when no provider is mounted, so a
 * consumer rendered outside the provider degrades to hiding the feature rather than throwing.
 */
export function useAIInsightsContext(): AIInsightsContextType {
    const context = useContext(AIInsightsContext);
    if (!context) {
        return {
            enabled: false,
            resolved: true,
            userEmail: '',
            aiInsightsService: undefined as unknown as AIInsightsService,
        };
    }
    return context;
}
